import { Component, OnInit, inject } from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TablerIconComponent } from "angular-tabler-icons";
import { from, take } from "rxjs";
import { BaseComponent } from "src/app/core/base/base.component";
import { MaterialModule } from "src/app/core/material.module";
import { HubServiceOrderCheckHistory } from "src/app/core/models/hub.model";
import { MenuServiceParameterizationResponse } from "src/app/core/models/menu-service.model";
import { Order } from "src/app/core/models/order";
import { OrderCheckStorage } from "src/app/core/models/order-check.model";
import { EventService } from "src/app/core/services/event.service";
import { HubService } from "src/app/core/services/hub.service";
import { MenuService } from "src/app/core/services/menu-service.service";
import { NotificationService } from "src/app/core/services/notification.service";
import { OrderCheckService } from "src/app/core/services/order-check.service";
import { OrderService } from "src/app/core/services/order.service";
import { environment } from "src/environments/environment";
import { BindTableBottomSheet } from "../../bottom-sheet/bind-table/bind-table.component";
import { CustomerNameBottomSheet } from "../../bottom-sheet/customer-name/customer-name.component";
import { OrderCheckObservationBottomSheet } from "../../bottom-sheet/ordercheck-observation/ordercheck-observation.component";
import { FontSizeComponent } from "../../font-size/font-size.component";
import { ProductsViewModeComponent } from "../../products-view-mode/products-view-mode.component";
import { ThemeModeComponent } from "../../theme-mode/theme-mode.component";

@Component({
  selector: "app-menu-configuration",
  imports: [
    MaterialModule,
    TablerIconComponent,
    FontSizeComponent,
    ProductsViewModeComponent,
    ThemeModeComponent,
  ],
  templateUrl: "./menu-configuration.component.html",
})
export class MenuConfigurationDialog extends BaseComponent implements OnInit {
  // Injections
  private orderCheckService = inject(OrderCheckService);
  private menuService = inject(MenuService);
  private hubService = inject(HubService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private eventService = inject(EventService);
  private orderService = inject(OrderService);
  private _bottomSheet = inject(MatBottomSheet);
  dialogRef = inject<MatDialogRef<MenuConfigurationDialog>>(MatDialogRef);

  // Variables
  urlToBack: string = window.location.href;
  appVersion = environment.version;
  orderCheckHistory: HubServiceOrderCheckHistory =
    new HubServiceOrderCheckHistory();
  parameterization: MenuServiceParameterizationResponse =
    this.orderCheckService.getParameterization();
  orderCheck: OrderCheckStorage = this.orderCheckService.getOrderCheckStorage();
  order: Order = this.orderService.getOrder(this.parameterization.CompanyId);
  selectedProductsCount = 0;
  isDarkMode = false;
  private skipUrlRestore = false;

  ngOnInit(): void {
    history.pushState(
      null,
      "Pedemais Delivery | Configurações",
      this.getSettingsUrl(),
    );

    this.onSubscribe();
  }

  override ngOnDestroy(): void {
    this.restoreUrl();
    super.ngOnDestroy();
  }

  //#region Events and synchronization
  onSubscribe() {
    this.subs.push(
      this.hubService.$orderCheckGetted.subscribe(
        (orderCheckHistory: HubServiceOrderCheckHistory) => {
          this.eventService.applyHubOrderCheckHistory(orderCheckHistory);
        },
      ),
    );

    this.subs.push(
      this.eventService.orderCheckHistoryChanged$.subscribe(
        (orderCheckHistory: HubServiceOrderCheckHistory) => {
          this.orderCheckHistory = orderCheckHistory;
        },
      ),
    );

    this.subs.push(
      this.eventService.orderChanged$.subscribe((order: Order) => {
        this.order = order;
      }),
    );

    this.subs.push(
      this.eventService.selectedProductsCountChanged$.subscribe(
        (selectedProductsCount: number) => {
          this.selectedProductsCount = selectedProductsCount;
        },
      ),
    );

    this.subs.push(
      this.menuService.onThemeApplyed$.subscribe((isDarkMode: boolean) => {
        this.isDarkMode = isDarkMode;
      }),
    );
  }

  onClose() {
    this.restoreUrl();
    this.dialogRef.close();
  }

  private getSettingsUrl(): string {
    return this.urlToBack.replace(/\/produtos(?=[/?#]|$)/, "/settings");
  }

  private restoreUrl(): void {
    if (this.skipUrlRestore || this.router.getCurrentNavigation()) {
      return;
    }

    const current = window.location.href;
    if (current === this.urlToBack || current !== this.getSettingsUrl()) {
      return;
    }

    history.pushState(null, document.title, this.urlToBack);
  }

  //#region Bottom Sheet
  openCustomerNameBottomSheet() {
    this._bottomSheet.open(CustomerNameBottomSheet, {
      data: {
        CustomerName: this.orderCheckHistory.customerName,
      },
      autoFocus: true,
    });

    this.onClose();
  }

  //Open Bind Table Bottom Sheet
  openBindTableBottomSheet() {
    this._bottomSheet.open(BindTableBottomSheet, {
      autoFocus: true,
    });

    this.onClose();
  }

  openOrderCheckObservationBottomSheet() {
    this._bottomSheet.open(OrderCheckObservationBottomSheet, {
      autoFocus: true,
    });
    this.onClose();
  }

  onChangeOrderCheck() {
    if (this.order.ItemsQuantity > 0) {
      from(
        this.notificationService.showConfirmBox(
          null,
          "Há produtos em seu carrinho, ao sair irá perder todos os item selecionados. Deseja continuar?",
          "warning",
        ),
      )
        .pipe(take(1), this.takeUntilDestroy())
        .subscribe((ret) => {
          if (ret.isConfirmed) {
            this.redirectToHomePage();
          }
        });
      return;
    }

    if (this.selectedProductsCount > 0) {
      from(
        this.notificationService.showConfirmBox(
          "",
          "Há produtos a serem adicionados ao seu carrinho, ao sair irá perder todos os item selecionados. Deseja continuar?",
          "warning",
        ),
      )
        .pipe(take(1), this.takeUntilDestroy())
        .subscribe((ret) => {
          if (ret.isConfirmed) {
            this.redirectToHomePage();
          }
        });
      return;
    }

    this.redirectToHomePage();
  }

  redirectToHomePage() {
    this.skipUrlRestore = true;
    this.dialogRef.close();
    this.router.navigate([
      "/",
      this.parameterization.FriendlyUrl,
      "comandaemesa",
    ]);
  }

  onLogout() {
    const companyId = this.parameterization?.CompanyId;

    this.orderCheckService.removeCollaborator();
    this.eventService.clearOrderCheckHistory();

    if (companyId) {
      this.orderService.cleanOrder(companyId);
      this.menuService.clearProductsCache(companyId);
    } else {
      this.menuService.clearProductsCache();
    }

    this.eventService.publishClearSelectedProducts();
    this.hubService.disconnect();
    this.skipUrlRestore = true;
    this.dialogRef.close();
    this.router.navigate(["/", this.parameterization.FriendlyUrl, "login"]);
  }
}
